import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { format } from 'date-fns';
import { api } from '@/lib/api';
import { START_PLANNING_DIALOG } from '@/constants/testIds';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

export default function StartPlanningDialog({ open, onOpenChange, item, onStarted }) {
    const navigate = useNavigate();
    const [name, setName] = useState('');
    const [start, setStart] = useState(undefined);
    const [end, setEnd] = useState(undefined);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (item) setName(`${item.destination} trip`);
    }, [item]);

    const reset = () => {
        setName(''); setStart(undefined); setEnd(undefined);
    };

    const submit = async () => {
        if (!name || !start || !end) { toast.error('Please fill in all fields'); return; }
        if (end < start) { toast.error('End date must be on or after start date'); return; }
        setLoading(true);
        try {
            const { data } = await api.post(`/wishlist/${item.wishlist_id}/start-planning`, {
                name,
                start_date: format(start, 'yyyy-MM-dd'),
                end_date: format(end, 'yyyy-MM-dd'),
            });
            toast.success('Trip created!');
            reset();
            onOpenChange(false);
            onStarted?.(data.trip);
            navigate(`/trips/${data.trip.trip_id}`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Failed');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
            <DialogContent className="rounded-3xl max-w-md border-[var(--ns-border)]" data-testid={START_PLANNING_DIALOG.root}>
                <DialogHeader>
                    <DialogTitle className="font-display text-2xl font-semibold tracking-tight">Start planning</DialogTitle>
                    <DialogDescription className="text-[var(--ns-text2)]">
                        Turn {item?.destination ? `"${item.destination}"` : 'this wishlist item'} into an active trip.
                    </DialogDescription>
                </DialogHeader>

                <div className="mt-2 space-y-4">
                    <div>
                        <Label className="text-sm text-[var(--ns-text2)]">Trip name</Label>
                        <Input
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="Trip name"
                            data-testid={START_PLANNING_DIALOG.nameInput}
                            className="h-12 rounded-xl mt-1 border-[var(--ns-border)] bg-white"
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
    <div>
        <Label className="text-sm text-[var(--ns-text2)]">Start date</Label>
        <input
            type="date"
            value={start ? format(start, 'yyyy-MM-dd') : ''}
            onChange={(e) => setStart(e.target.value ? new Date(e.target.value + 'T00:00:00') : undefined)}
            data-testid={START_PLANNING_DIALOG.startDateButton}
            className="w-full h-12 rounded-xl mt-1 border border-[var(--ns-border)] bg-white px-3 text-sm"
        />
    </div>
    <div>
        <Label className="text-sm text-[var(--ns-text2)]">End date</Label>
        <input
            type="date"
            value={end ? format(end, 'yyyy-MM-dd') : ''}
            onChange={(e) => setEnd(e.target.value ? new Date(e.target.value + 'T00:00:00') : undefined)}
            data-testid={START_PLANNING_DIALOG.endDateButton}
            className="w-full h-12 rounded-xl mt-1 border border-[var(--ns-border)] bg-white px-3 text-sm"
        />
    </div>
</div>
                </div>

                <DialogFooter className="mt-6">
                    <Button
                        onClick={submit}
                        disabled={loading}
                        data-testid={START_PLANNING_DIALOG.submitButton}
                        className="w-full h-12 rounded-full text-white font-medium"
                        style={{ background: 'var(--ns-navy)' }}
                    >
                        {loading ? 'Creating…' : 'Start planning'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
